import { useState } from "react";
import { Link } from "react-router-dom";
import { Menu, X, Phone, LogOut } from "lucide-react";
import Logo from "./Logo";
import { useAuth } from "../context/AuthContext";

const NAV = [
  ["The Truth", "#truth"],
  ["Shattered Glass", "#poem"],
  ["Stories", "#stories"],
  ["Whānau", "#whanau"],
  ["Evidence", "#evidence"],
  ["Pledge", "#pledge"],
  ["The Plan", "#plan"],
  ["Partner", "#support"],
];

export function HelplineBar() {
  return (
    <div data-testid="emergency-helpline-bar" className="fixed top-0 inset-x-0 z-50 bg-[#B91C1C] text-white">
      <div className="max-w-7xl mx-auto px-4 h-9 flex items-center justify-between gap-2">
        <span className="mono-label hidden md:block opacity-90">Need to talk? 24/7 · free · confidential</span>
        <div className="flex items-center gap-2 ml-auto">
          <a data-testid="helpline-1737-button" href="tel:1737" className="flex items-center gap-1.5 text-[11px] sm:text-xs font-semibold bg-white/15 hover:bg-white/25 transition-colors rounded-full px-3 py-1">
            <Phone size={12} /> Call / text 1737
          </a>
          <a data-testid="helpline-0800-button" href="tel:0800787797" className="hidden sm:flex items-center gap-1.5 text-[11px] sm:text-xs font-semibold bg-white/15 hover:bg-white/25 transition-colors rounded-full px-3 py-1">
            <Phone size={12} /> 0800 METH HELP
          </a>
          <a data-testid="helpline-text-8681" href="sms:8681" className="hidden md:flex items-center gap-1.5 text-[11px] sm:text-xs font-semibold bg-white/15 hover:bg-white/25 transition-colors rounded-full px-3 py-1">
            Text 8681
          </a>
          <button data-testid="safety-exit-button" onClick={() => window.location.replace("https://www.google.com")} className="flex items-center gap-1.5 text-[11px] sm:text-xs font-semibold bg-black/30 hover:bg-black/50 transition-colors rounded-full px-3 py-1">
            <LogOut size={12} /> Quick exit
          </button>
        </div>
      </div>
    </div>
  );
}

export function Navbar() {
  const [open, setOpen] = useState(false);
  const { user, logout } = useAuth();
  return (
    <header className="fixed top-9 inset-x-0 z-40 border-b border-white/10 bg-[#090A0F]/70 backdrop-blur-xl">
      <div className="max-w-7xl mx-auto px-4 h-16 flex items-center justify-between">
        <a href="#top" className="flex items-center gap-3" data-testid="nav-brand">
          <Logo size={30} />
          <span className="font-display font-extrabold uppercase tracking-tight text-sm leading-none text-white">
            No To Meth
            <span className="block text-[10px] font-grotesk font-medium normal-case tracking-[0.18em] text-slate-400">Ōtautahi Christchurch</span>
          </span>
        </a>
        <nav className="hidden lg:flex items-center gap-6">
          {NAV.map(([label, href]) => (
            <a key={href} href={href} data-testid={`nav-${label.toLowerCase().replace(/\s/g, "-")}-link`} className="text-xs font-grotesk font-medium text-slate-300 hover:text-white transition-colors">
              {label}
            </a>
          ))}
        </nav>
        <div className="flex items-center gap-3">
          {user ? (
            <>
              {user.role === "admin" && <Link to="/admin" data-testid="nav-admin-link" className="text-xs font-grotesk text-amber-400 hover:text-amber-300">Admin</Link>}
              {(user.role === "admin" || user.role === "team") && <Link to="/tracker" data-testid="nav-tracker-link" className="text-xs font-grotesk text-emerald-400 hover:text-emerald-300">Tracker</Link>}
              <button data-testid="nav-logout-button" onClick={logout} className="text-xs font-grotesk text-slate-400 hover:text-white">Log out</button>
            </>
          ) : (
            <Link to="/login" data-testid="nav-login-link" className="text-xs font-grotesk text-slate-400 hover:text-white hidden sm:block">Sign in</Link>
          )}
          <a href="#pledge" data-testid="nav-pledge-cta" className="hidden sm:inline-block bg-[#EF4444] hover:bg-[#DC2626] transition-colors text-white text-xs font-bold rounded-full px-4 py-2">
            Sign the pledge
          </a>
          <button data-testid="nav-menu-toggle" className="lg:hidden text-white" onClick={() => setOpen(!open)} aria-label="Menu">
            {open ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </div>
      {open && (
        <nav className="lg:hidden border-t border-white/10 bg-[#090A0F]/95 backdrop-blur-xl px-4 py-4 flex flex-col gap-3">
          {NAV.map(([label, href]) => (
            <a key={href} href={href} onClick={() => setOpen(false)} className="text-sm font-grotesk text-slate-200">{label}</a>
          ))}
          {!user && <Link to="/login" className="text-sm font-grotesk text-slate-200">Sign in</Link>}
        </nav>
      )}
    </header>
  );
}
